import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Button } from "../ui/button";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Referrals", path: "/referrals" },
  { name: "Safeguarding", path: "/safeguarding" },
  { name: "Impact", path: "/impact" },
  { name: "Work With Us", path: "/work-with-us" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      {/* Top bar */}
      <div className="bg-slate-900 text-white py-2 text-sm hidden md:block">
        <div className="container-custom flex justify-end items-center gap-6">
          <a
            href="mailto:info@epiccareltd.co.uk"
            className="flex items-center gap-2 hover:text-teal-400 transition-colors"
            data-testid="header-email"
          >
            <Mail className="w-4 h-4" />
            info@epiccareltd.co.uk
          </a>
          <a
            href="tel:+441onal"
            className="flex items-center gap-2 hover:text-teal-400 transition-colors"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4" />
            Contact Us
          </a>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled
            ? "bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-100"
            : "bg-white"
        }`}
        data-testid="main-header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="header-logo"
            >
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">EC</span>
              </div>
              <span className="font-bold text-xl text-slate-900 hidden sm:inline">
                Epic Care
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === link.path
                      ? "text-teal-600 bg-teal-50"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="flex items-center gap-3">
              <Link to="/referrals" className="hidden sm:block">
                <Button
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
                  data-testid="header-referral-btn"
                >
                  Make a Referral
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-100"
              data-testid="mobile-menu"
            >
              <nav className="container-custom py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg font-medium ${
                      location.pathname === link.path
                        ? "text-teal-600 bg-teal-50"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link to="/referrals" className="block pt-2">
                  <Button
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                    data-testid="mobile-referral-btn"
                  >
                    Make a Referral
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
